import { useState, useMemo, useEffect } from 'react';
import { X, Check, Target, Search, MapPin, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { SECTEURS, STATUTS, StatutBadge, SecteurTag, ScoreBadge, PriorityDot } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

export function CrmPipeline({ prospects, onSelect, onBulkUpdate, onBulkDelete, onRefresh }) {
  const [filterStatut, setFilterStatut] = useState('');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [labels, setLabels] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/crm/labels`).then(r => r.json()).then(setLabels).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return prospects.filter(p => {
      if (filterStatut && p.statut !== filterStatut) return false;
      if (filterSecteur && p.secteur !== filterSecteur) return false;
      if (filterTag && !(p.tags || []).includes(filterTag)) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        return (p.nom || '').toLowerCase().includes(q) || (p.contact || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q) || (p.ville || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [prospects, filterStatut, filterSecteur, filterTag, searchText]);

  const today = new Date().toISOString().split('T')[0];
  const toggleSelect = (id) => { setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const selectAll = () => { if (selectedIds.size === filtered.length) setSelectedIds(new Set()); else setSelectedIds(new Set(filtered.map(p => p.id))); };
  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    const ids = [...selectedIds];
    if (bulkAction === 'delete') { if (!window.confirm(`Supprimer ${ids.length} prospect(s) ?`)) return; await onBulkDelete(ids); }
    else if (bulkAction.startsWith('label:')) {
      const labelId = bulkAction.slice(6);
      await fetch(`${API}/api/crm/prospects/bulk/labels`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospect_ids: ids, label_id: labelId, action: 'add' }),
      });
      if (onRefresh) onRefresh();
    }
    else if (bulkAction.startsWith('unlabel:')) {
      const labelId = bulkAction.slice(8);
      await fetch(`${API}/api/crm/prospects/bulk/labels`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospect_ids: ids, label_id: labelId, action: 'remove' }),
      });
      if (onRefresh) onRefresh();
    }
    else { await onBulkUpdate(ids, { statut: bulkAction }); }
    setSelectedIds(new Set()); setBulkAction('');
  };

  const kanbanGroups = useMemo(() => {
    if (viewMode !== 'kanban') return {};
    const groups = {}; Object.keys(STATUTS).forEach(s => { groups[s] = []; });
    filtered.forEach(p => { const s = p.statut || 'nouveau'; if (groups[s]) groups[s].push(p); });
    return groups;
  }, [filtered, viewMode]);

  return (
    <div className="space-y-3" data-testid="crm-pipeline">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300" />
          <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Rechercher..." className="pl-8 text-xs h-9" data-testid="pipeline-search" />
        </div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setViewMode('list')} className={`px-2 py-1 rounded text-[10px] font-medium ${viewMode === 'list' ? 'bg-white shadow-sm text-[#1B2A52]' : 'text-gray-400'}`}>Liste</button>
          <button onClick={() => setViewMode('kanban')} className={`px-2 py-1 rounded text-[10px] font-medium ${viewMode === 'kanban' ? 'bg-white shadow-sm text-[#1B2A52]' : 'text-gray-400'}`}>Kanban</button>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="filter-statut">
          <option value="">Tous statuts</option>
          {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterSecteur} onChange={e => setFilterSecteur(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="filter-secteur">
          <option value="">Tous secteurs</option>
          {Object.entries(SECTEURS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterTag} onChange={e => setFilterTag(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="filter-tag">
          <option value="">Tous libelles</option>
          {labels.map(l => <option key={l.id} value={l.id}>🏷️ {l.label}</option>)}
        </select>
        <span className="text-xs text-gray-400 self-center whitespace-nowrap">{filtered.length} prospect{filtered.length > 1 ? 's' : ''}</span>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 bg-[#1B2A52] text-white rounded-xl px-4 py-2.5 flex-wrap" data-testid="bulk-bar">
          <span className="text-xs font-medium">{selectedIds.size} selectionne{selectedIds.size > 1 ? 's' : ''}</span>
          <div className="flex-1" />
          <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} className="text-xs bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-white">
            <option value="" style={{ color: '#000' }}>Action...</option>
            <optgroup label="Statut" style={{ color: '#000' }}>
              {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>-&gt; {v.label}</option>)}
            </optgroup>
            <optgroup label="Ajouter libelle" style={{ color: '#000' }}>
              {labels.map(l => <option key={`add-${l.id}`} value={`label:${l.id}`}>+ {l.label}</option>)}
            </optgroup>
            <optgroup label="Retirer libelle" style={{ color: '#000' }}>
              {labels.map(l => <option key={`rem-${l.id}`} value={`unlabel:${l.id}`}>- {l.label}</option>)}
            </optgroup>
            <option value="delete" style={{ color: '#000' }}>Supprimer</option>
          </select>
          <Button onClick={handleBulkAction} size="sm" className="bg-white/20 hover:bg-white/30 text-white text-xs h-7" disabled={!bulkAction}>OK</Button>
          <button onClick={() => setSelectedIds(new Set())} className="text-white/60 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {viewMode === 'list' ? (
        <>
          {filtered.length > 0 && <button onClick={selectAll} className="text-[10px] text-gray-400 hover:text-[#1B2A52]">{selectedIds.size === filtered.length ? 'Tout deselectionner' : 'Tout selectionner'}</button>}
          {filtered.length === 0 && <div className="text-center py-12 text-gray-400"><Target size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">Aucun prospect</p></div>}
          {filtered.map(p => {
            const isLate = p.relanceDate && p.relanceDate <= today && !['gagne', 'perdu'].includes(p.statut);
            const isSelected = selectedIds.has(p.id);
            return (
              <div key={p.id} className={`bg-white rounded-xl border p-3 cursor-pointer transition-all hover:shadow-md ${isLate ? 'border-red-200 bg-red-50/20' : ''} ${isSelected ? 'ring-2 ring-[#1B2A52]/30' : ''}`} data-testid={`prospect-card-${p.id}`}>
                <div className="flex items-start gap-2">
                  <button onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }} className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 shrink-0 ${isSelected ? 'border-[#1B2A52] bg-[#1B2A52]' : 'border-gray-200'}`}>
                    {isSelected && <Check size={10} className="text-white" />}
                  </button>
                  <div className="flex-1 min-w-0" onClick={() => onSelect(p)}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <PriorityDot priorite={p.priorite} />
                      <p className="text-sm font-bold text-gray-900 truncate">{p.nom}</p>
                      <ScoreBadge score={p.scoreIA} />
                      {isLate && <span className="text-[9px] px-1.5 py-0.5 bg-red-500 text-white rounded-full font-bold animate-pulse">RETARD</span>}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{p.contact}{p.poste ? ` \u2014 ${p.poste}` : ''}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <SecteurTag secteur={p.secteur} />
                      <StatutBadge statut={p.statut} size="xs" />
                      {(p.tags || []).map(tagId => {
                        const lb = labels.find(l => l.id === tagId);
                        if (!lb) return null;
                        return (
                          <span key={tagId} className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${lb.color}22`, color: lb.color, border: `1px solid ${lb.color}44` }}>
                            🏷️ {lb.label}
                          </span>
                        );
                      })}
                      {p.devisAmount && <span className="text-[10px] font-mono text-[#C9A84C] font-bold">{Number(p.devisAmount).toLocaleString('fr-FR')}EUR</span>}
                      {p.ville && <span className="text-[10px] text-gray-300"><MapPin size={9} className="inline" /> {p.ville}</span>}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-gray-200 shrink-0 mt-2" />
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4" data-testid="kanban-view">
          <div className="flex gap-3" style={{ minWidth: '1200px' }}>
            {Object.entries(STATUTS).filter(([k]) => k !== 'perdu').map(([key, s]) => (
              <div key={key} className="flex-1 min-w-[160px]">
                <div className="flex items-center gap-1.5 mb-2 px-1"><div className="w-2 h-2 rounded-full" style={{ background: s.color }} /><span className="text-[10px] font-bold text-gray-500 uppercase">{s.label}</span><span className="text-[10px] text-gray-300 ml-auto">{(kanbanGroups[key] || []).length}</span></div>
                <div className="space-y-2 min-h-[100px] bg-gray-50/50 rounded-xl p-1.5">
                  {(kanbanGroups[key] || []).map(p => (
                    <div key={p.id} onClick={() => onSelect(p)} className="bg-white rounded-lg border p-2.5 cursor-pointer hover:shadow-sm">
                      <p className="text-xs font-bold text-gray-900 truncate">{p.nom}</p>
                      <p className="text-[10px] text-gray-400 truncate mt-0.5">{p.contact}</p>
                      <div className="flex items-center gap-1 mt-1.5"><SecteurTag secteur={p.secteur} /><ScoreBadge score={p.scoreIA} /></div>
                      {p.devisAmount && <p className="text-[10px] font-mono text-[#C9A84C] mt-1">{Number(p.devisAmount).toLocaleString('fr-FR')}EUR</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
